import {
  FALLBACK,
  makePriceLookup,
  normalize,
  type ModelPrice,
  type PriceLookup,
  type PriceTable,
} from "../lib/pricing-table";

/**
 * The browser's price table.
 *
 * The reader must price tokens from the *same* feed the local dashboard uses, or
 * the two disagree about what identical history is worth — the exact drift the
 * isomorphic pricing split was made to prevent. So this fetches LiteLLM directly
 * rather than settling for the built-in rates.
 *
 * `pricing.ts` does the same job for Node against a file cache. The table and
 * the resolution rules are shared; only the storage differs.
 */

const LITELLM_URL =
  "https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json";
const CACHE_KEY = "statsai.prices.v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface CachedTable {
  fetchedAt: number;
  table: PriceTable;
}

/** Where the current prices came from, so the UI can be honest about it. */
export type PriceSource = "live" | "cached" | "built-in";

export interface Prices {
  lookup: PriceLookup;
  source: PriceSource;
  /** Number of models priced. */
  count: number;
}

/**
 * Resolve a price lookup, preferring fresh LiteLLM data and degrading quietly.
 *
 * Never throws: a dashboard that refuses to render because a price feed is
 * unreachable would be trading a small inaccuracy for total unavailability.
 * Built-in rates cover the models actually in use here, so the fallback is
 * approximate rather than wrong.
 */
export async function loadPrices(): Promise<Prices> {
  const cached = readCache();
  if (cached && Date.now() - cached.fetchedAt < MAX_AGE_MS) {
    return table(cached.table, "cached");
  }

  try {
    const fresh = await fetchTable();
    writeCache({ fetchedAt: Date.now(), table: fresh });
    return table(fresh, "live");
  } catch {
    // Offline, blocked, or CORS-refused. A stale cache still beats built-ins.
    if (cached) return table(cached.table, "cached");
    return table(FALLBACK, "built-in");
  }
}

function table(t: PriceTable, source: PriceSource): Prices {
  // Built-ins underneath so a model missing from the feed still prices.
  const merged = source === "built-in" ? t : { ...FALLBACK, ...t };
  return { lookup: makePriceLookup(merged), source, count: Object.keys(merged).length };
}

const FETCH_TIMEOUT_MS = 15_000;

async function fetchTable(): Promise<PriceTable> {
  // Bounded, because the page cannot render a figure until prices resolve. An
  // unbounded fetch against a stalled connection doesn't degrade to built-in
  // rates — it never resolves at all, and the dashboard waits forever.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(LITELLM_URL, { signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const raw = (await res.json()) as Record<string, Record<string, number>>;

  // Trimmed to the five fields cost needs, keyed by normalized id — the raw feed
  // is megabytes and would not survive a localStorage quota.
  const trimmed: PriceTable = {};
  for (const [name, info] of Object.entries(raw)) {
    if (typeof info?.input_cost_per_token !== "number") continue;
    const input = info.input_cost_per_token;
    const key = normalize(name);
    // LiteLLM omits cache_creation cost on several Claude entries, but Anthropic
    // bills 5m cache writes at 1.25x input — without this, those tokens cost $0.
    const cacheWrite =
      info.cache_creation_input_token_cost ??
      (key.startsWith("claude") ? input * 1.25 : 0);
    trimmed[key] = {
      input,
      output: info.output_cost_per_token ?? 0,
      cacheRead: info.cache_read_input_token_cost ?? 0,
      cacheWrite,
      cacheWrite1h:
        info.cache_creation_input_token_cost_above_1hr ??
        (input > 0 ? input * 2 : cacheWrite),
    } satisfies ModelPrice;
  }
  return trimmed;
}

function readCache(): CachedTable | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedTable;
    return typeof parsed?.fetchedAt === "number" && parsed.table ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(c: CachedTable): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    // Over quota or unavailable — prices still work for this session.
  }
}
